import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Moon, Share2, Sun, Terminal } from 'lucide-react';
import type { DetrixSession } from '../types';
import { usePlaybackStore } from '../store/usePlaybackStore';
import { useSessionUiStore } from '../store/useSessionUiStore';
import { useThemeStore } from '../store/useThemeStore';
import {
  countSessionIssues,
  findFirstErrorInSession,
  listSessionErrors,
  type SessionErrorListItem,
} from '../utils/sessionErrorSignals';
import { formatSessionOffset } from '../utils/time';
import { copyShareLinkForSession, getSessionApiBase } from '../utils/shareSessionLink';

interface ToolbarProps {
  session: DetrixSession;
}

export const Toolbar: React.FC<ToolbarProps> = ({ session }) => {
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const { metadata, sessionId } = session;
  const issues = useMemo(() => countSessionIssues(session), [session]);
  const errorItems = useMemo(() => listSessionErrors(session), [session]);
  const [issueMenuOpen, setIssueMenuOpen] = useState(false);
  const issueAnchorRef = useRef<HTMLDivElement>(null);
  const shareApiReady = Boolean(getSessionApiBase());
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [shareErrorDetail, setShareErrorDetail] = useState<string | null>(null);

  useEffect(() => {
    if (!issueMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!issueAnchorRef.current?.contains(e.target as Node)) setIssueMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [issueMenuOpen]);

  const jumpToErrorItem = (item: SessionErrorListItem) => {
    const ui = useSessionUiStore.getState();
    const pb = usePlaybackStore.getState();
    ui.setActiveTab(item.tab);
    ui.setSelectedId(item.id);
    pb.setPlayheadMs(item.time, { pause: true });
    setIssueMenuOpen(false);
  };

  const jumpToFirstError = () => {
    const target = findFirstErrorInSession(session);
    if (!target) return;
    const ui = useSessionUiStore.getState();
    const pb = usePlaybackStore.getState();
    ui.setActiveTab(target.tab);
    ui.setSelectedId(target.id);
    pb.setPlayheadMs(target.time, { pause: true });
    setIssueMenuOpen(false);
  };

  const tabLabel = (tab: SessionErrorListItem['tab']) => {
    switch (tab) {
      case 'steps':
        return 'Steps';
      case 'actions':
        return 'Actions';
      case 'network':
        return 'Network';
      case 'events':
        return 'Events';
      default:
        return tab;
    }
  };

  return (
    <header className="sticky top-0 z-50 flex h-10 items-center gap-2 border-b bg-[var(--bg-sidebar)] px-3 shadow-sm">
      <div className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-[var(--accent)]" />
          <span className="font-semibold tracking-tight text-[var(--text-primary)]">
            Detrix <span className="text-[var(--text-muted)]">·</span> Studio
          </span>
        </div>

        <div className="h-4 w-px bg-[var(--border)]" />

        <div className="flex items-center gap-3 overflow-hidden">
          <span className="mono-small truncate text-[var(--text-muted)]">{sessionId.slice(0, 12)}…</span>

          <div className="hidden items-center gap-2 md:flex">
            <div className="flex items-center gap-1 rounded border border-[var(--border)] bg-[var(--bg-editor)] px-2 py-0.5 text-[11px] text-[var(--text-primary)]">
              <span className="text-[var(--text-muted)]">https://</span>
              <span className="max-w-[150px] truncate">{metadata.startUrl.replace('https://', '')}</span>
              <span className="mx-0.5 text-[var(--text-muted)]">→</span>
              <span className="max-w-[150px] truncate">
                {metadata.endUrl?.replace('https://', '') ?? '…'}
              </span>
            </div>

            <div className="ml-2 flex items-center gap-3 text-[11px] text-[var(--text-secondary)]">
              <span>{metadata.environment.platform}</span>
              <span>
                {metadata.environment.viewport.width} × {metadata.environment.viewport.height}
              </span>
              <span>Chrome</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {issues.errors > 0 ? (
          <div className="relative shrink-0" ref={issueAnchorRef}>
            <button
              type="button"
              aria-expanded={issueMenuOpen}
              aria-haspopup="menu"
              onClick={() => setIssueMenuOpen((o) => !o)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setIssueMenuOpen(false);
              }}
              title={
                issues.errors > 1
                  ? `Open list of ${issues.errors} errors (chronological)`
                  : 'Open error details'
              }
              className="inline-flex max-w-[min(11rem,42vw)] items-center gap-1.5 rounded border border-[var(--toolbar-error-border)] bg-[var(--toolbar-error-bg)] px-2 py-0.5 text-[11px] transition-colors hover:border-[var(--toolbar-error-border-hover)] hover:bg-[var(--toolbar-error-bg-hover)]"
            >
              <AlertCircle className="h-3.5 w-3.5 shrink-0 text-[var(--toolbar-error-icon)]" aria-hidden />
              <span className="truncate font-semibold tabular-nums text-[var(--toolbar-error-text)]">
                {issues.errors} error{issues.errors === 1 ? '' : 's'}
              </span>
              {issues.warnings > 0 ? (
                <span className="hidden shrink-0 border-l border-[var(--toolbar-error-border)] pl-2 tabular-nums text-[var(--toolbar-warn-inline-text)] sm:inline">
                  {issues.warnings} warn
                </span>
              ) : null}
            </button>

            {issueMenuOpen && errorItems.length > 0 ? (
              <div
                role="menu"
                aria-label="Session errors"
                className="absolute right-0 top-[calc(100%+4px)] z-[200] flex max-h-[min(70vh,26rem)] w-[min(22rem,calc(100vw-1.25rem))] flex-col overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-sidebar)] shadow-xl ring-1 ring-[var(--menu-ring)]"
              >
                <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[var(--border-subtle)] px-2 py-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                    All errors ({errorItems.length})
                  </span>
                  <button
                    type="button"
                    className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium text-[var(--accent)] hover:bg-[var(--toolbar-menu-hover)]"
                    onClick={jumpToFirstError}
                  >
                    First
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-0.5">
                  {errorItems.map((item) => (
                    <button
                      key={`${item.tab}-${item.id}`}
                      type="button"
                      role="menuitem"
                      onClick={() => jumpToErrorItem(item)}
                      className="flex w-full flex-col gap-0.5 border-b border-[var(--border-subtle)]/80 px-2 py-1.5 text-left last:border-b-0 hover:bg-[var(--bg-elevated)]"
                    >
                      <span className="font-mono text-[10px] tabular-nums text-[var(--text-muted)]">
                        {formatSessionOffset(item.time)} · {tabLabel(item.tab)}
                      </span>
                      <span className="line-clamp-2 text-[11px] leading-snug text-[var(--text-primary)]">{item.summary}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : issues.warnings > 0 ? (
          <div className="hidden items-center gap-1.5 rounded border border-[var(--toolbar-warn-border)] bg-[var(--toolbar-warn-bg)] px-2 py-0.5 text-[11px] text-[var(--toolbar-warn-text)] sm:inline-flex">
            <span className="tabular-nums">
              {issues.warnings} warning{issues.warnings === 1 ? '' : 's'}
            </span>
          </div>
        ) : null}

        <button
          type="button"
          disabled={!shareApiReady || shareBusy}
          aria-label="Copy share link"
          title={
            !shareApiReady
              ? 'Set VITE_SESSION_API_BASE (see MANUAL_TEST.md), then restart Vite or rebuild.'
              : shareErrorDetail ?? 'Copy shareable link to clipboard'
          }
          onClick={() => {
            void (async () => {
              setShareBusy(true);
              setShareCopied(false);
              setShareErrorDetail(null);
              const r = await copyShareLinkForSession(session);
              setShareBusy(false);
              if (r.ok) {
                setShareCopied(true);
                window.setTimeout(() => setShareCopied(false), 2000);
              } else {
                setShareErrorDetail(r.error);
              }
            })();
          }}
          className="flex max-w-[min(100%,12rem)] items-center gap-1.5 rounded border border-[var(--border)] bg-[var(--bg-elevated)] px-2 py-1 text-[11px] font-medium text-[var(--text-primary)] transition-colors hover:bg-[var(--border)] disabled:cursor-not-allowed disabled:opacity-45"
        >
          <Share2 className="h-3 w-3 shrink-0 text-[var(--accent)]" strokeWidth={2} aria-hidden />
          <span className="hidden min-w-0 truncate sm:inline">
            {shareBusy ? '…' : shareCopied ? 'Copied' : 'Share link'}
          </span>
          <span className="truncate sm:hidden">{shareBusy ? '…' : shareCopied ? 'OK' : 'Share'}</span>
        </button>

        <button
          type="button"
          aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          title={theme === 'dark' ? 'Light theme' : 'Dark theme'}
          onClick={() => toggleTheme()}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-[var(--border)] bg-[var(--bg-elevated)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--border)] hover:text-[var(--text-primary)]"
        >
          {theme === 'dark' ? <Sun className="h-3.5 w-3.5" aria-hidden /> : <Moon className="h-3.5 w-3.5" aria-hidden />}
        </button>
      </div>
    </header>
  );
};
